<template>
  <div class="view marketing campaign create">
    <div class="page">
      <a-page-header
        class="rounded bg-gray-1"
        :title="'Campaign Details'"
        :sub-title="`${campaign.name ? campaign.name : ''}`"
        @back="() => $router.push('/marketing/campaigns')"
      >
        <template slot="tags">
          <a-tag :color="'blue'">{{ campaign.notification_channel | CAPITALIZE_ }}</a-tag>
          <a-tag :color="campaign.status | CAMPAIGN_STATUS_COLOR_">{{ campaign.status | CAMPAIGN_STATUS_ }}</a-tag>
        </template>

        <template slot="extra">
          <a-button key="0" type="danger"> Delete </a-button>
          <a-button key="1" type="primary" @click="onCreate"> Create </a-button>
        </template>

        <a-descriptions :column="2" v-if="campaign._id">
          <a-descriptions-item label="Campaign ID">
            <input class="idbox" :value="campaign._id" />
          </a-descriptions-item>
          <a-descriptions-item label="Created By" v-if="campaign.created_by">
            {{ campaign.created_by.display_name }}
          </a-descriptions-item>
          <a-descriptions-item label="Updated by" v-if="campaign.updated_by">
            {{ campaign.updated_by.display_name }}
          </a-descriptions-item>
          <a-descriptions-item label="Created At" v-if="campaign.created_at">
            <span class="badge badge-primary"> {{ campaign.created_at | moment('lll') }}</span>
          </a-descriptions-item>
          <a-descriptions-item label="Updated At" v-if="campaign.updated_at">
            <span class="badge badge-success"> {{ campaign.updated_at | moment('lll') }}</span>
          </a-descriptions-item>
        </a-descriptions>
      </a-page-header>

      <a-tabs v-model="currentTab" @change="onTagChange">
        <!-- Tab:Meta -->
        <a-tab-pane key="setting" tab="Setting">
          <div class="content mt-4">
            <!-- form -->
            <a-form-model
              ref="campaignForm"
              :model="campaign"
              :rules="rules"
              :label-col="labelCol"
              :wrapper-col="wrapperCol"
            >
              <!-- Notification Channel -->
              <a-form-model-item label="Notification Channel" prop="notification_channel">
                <a-radio-group v-model="campaign.notification_channel" default-value="email" button-style="solid">
                  <a-radio-button value="email"> Email </a-radio-button>
                  <a-radio-button value="sms"> SMS </a-radio-button>
                </a-radio-group>
              </a-form-model-item>

              <!-- Sender -->
              <a-form-model-item label="Sender" prop="sender">
                <a-input v-model="campaign.sender" placeholder="Sender of the campaign" />
              </a-form-model-item>

              <!-- Recipients Group -->
              <a-form-model-item label="Recipients Group" prop="recipient_group">
                <a-select v-model="campaign.recipient_group" default-value="all">
                  <a-select-option v-for="group in recipient_groups" :key="group._id" :value="group._id">
                    {{ group.name }} ({{ group.recipients.length }})
                  </a-select-option>
                </a-select>
              </a-form-model-item>

              <!-- Campaign Title -->
              <a-form-model-item label="Campaign Title" prop="title">
                <a-input v-model="campaign.title" placeholder="Title of the campaign" />
              </a-form-model-item>

              <!-- Subtitle -->
              <a-form-model-item label="Subtitle" prop="subtitle">
                <a-input v-model="campaign.subtitle" placeholder="Subtitle" />
              </a-form-model-item>

              <!-- Campaign Content(SMS) -->
              <template v-if="campaign.notification_channel === 'sms'">
                <a-form-model-item label="SMS Content" prop="text">
                  <a-input v-model="campaign.text" type="textarea" :rows="12" placeholder="Campaign content..." />
                </a-form-model-item>
              </template>

              <!-- Preset Template -->
              <template v-if="campaign.notification_channel === 'email'">
                <a-form-model-item label="Preset Template" prop="template">
                  <a-select v-model="campaign.template" default-value="default">
                    <a-select-option value="default"> Default </a-select-option>
                    <a-select-option value="template1"> Template 1 </a-select-option>
                    <a-select-option value="template2"> Template 2 </a-select-option>
                    <a-select-option value="template3"> Template 3 </a-select-option>
                    <a-select-option value="template4"> Template 4 </a-select-option>
                    <a-select-option value="template5"> Template 5 </a-select-option>
                    <a-select-option value="template6"> Template 6 </a-select-option>
                  </a-select>
                </a-form-model-item>

                <!-- Campaign Content(Email) -->
                <a-form-item label="Email Content">
                  <a-button type="primary" @click="currentTab = 'design'" icon="edit">Design</a-button>
                  <a-button class="ml-2" type="primary" @click="currentTab = 'preview'" icon="eye">Preview</a-button>
                </a-form-item>
              </template>

              <a-form-model-item label="Scheduled Date" prop="scheduled">
                <a-date-picker
                  v-model="campaign.scheduled"
                  show-time
                  type="date"
                  placeholder="Pick a scheduled time"
                  style="width: 100%"
                />
              </a-form-model-item>

              <a-form-model-item label="Description" prop="description">
                <a-input
                  v-model="campaign.description"
                  type="textarea"
                  :rows="12"
                  placeholder="Campaign description..."
                />
              </a-form-model-item>

              <!-- actions -->
              <a-form-model-item :wrapper-col="{ span: 14, offset: 4 }">
                <a-button type="primary" @click="onCreate"> Create </a-button>
              </a-form-model-item>
            </a-form-model>
          </div>
        </a-tab-pane>

        <!-- Tab:Design -->
        <a-tab-pane key="design" tab="Email Design" v-if="campaign.notification_channel === 'email'">
          <div class="design" v-if="currentTab === 'design'">
            <EmailBuilder :design="campaign.design" @onAutoSaving="onAutoSaving" />
          </div>
        </a-tab-pane>

        <!-- Tab:Preview -->
        <a-tab-pane key="preview" tab="Email Preview" forceRender v-if="campaign.notification_channel === 'email'">
          <div class="preview" v-if="campaign.html">
            <div class="inner">
              <div v-html="campaign.html"></div>
            </div>
          </div>
          <div class="no-preview" v-else>NO PREVIEW</div>
        </a-tab-pane>
      </a-tabs>
    </div>
  </div>
</template>

<script>
import { EmailBuilder } from '@/components/reno/general'
import router from '@/router'
import store from '@/store'
const KEY = 'campaign'

export default {
  components: {
    EmailBuilder,
  },
  data() {
    return {
      campaign: {
        status: 'draft',
        notification_channel: 'email',
      },
      handleButton: {
        text: 'Schedule',
        type: 'primary',
      },
      currentTab: 'setting',
      size: 'large',
      labelCol: { xs: 24, sm: 4 },
      wrapperCol: { xs: 24, sm: 20 },
      rules: {
        notification_channel: [{ required: true, message: 'Please select notification channel', trigger: 'blur' }],
        sender: [{ required: true, message: 'Please input campaign sender', trigger: 'blur' }],
        recipient_group: [{ required: true, message: 'Please select recipient group', trigger: 'blur' }],
        title: [
          { required: true, message: 'Please input campaign title', trigger: 'blur' },
          { min: 3, max: 120, message: 'Length should be 3 to 120', trigger: 'blur' },
        ],
        sender: [{ required: true, message: 'Please input campaign sender', trigger: 'blur' }],
      },
      recipient_groups: [],
    }
  },
  methods: {
    async fetch() {
      this.recipient_groups = await this.$store.dispatch('marketing/fetchRecipientGroups')
    },
    onTagChange(val) {
      // console.log(val)
    },
    onAutoSaving(data) {
      this.campaign.design = data.design
      this.campaign.html = data.html
    },

    async onCreate() {
      this.$refs.campaignForm.validate(async (valid) => {
        if (valid) {
          const campaign = await store.dispatch('marketing/createCampaign', this.campaign)
          if (campaign) {
            this.$message.success(`Campaign ${campaign.title} has been updated successfully!`)
            router.push('/marketing/campaigns')
          }
        } else {
          this.$message.error('Oops, The campaign creation failed!')
          return false
        }
      })
    },
  },
  async created() {
    await this.fetch()
  },
  async mounted() {},
}
</script>

<style lang="scss" scoped>
.unlayer-editor {
  height: 800px;
}

.preview {
  .inner {
    border: 1px solid #ddd;
    border-radius: 3px;
    padding: 1rem;
  }
}
</style>
